import { inferBaseUrl } from '~/api/client.js';
import bundledDocuments from './documents.json';

export interface LegalDocument {
  readonly slug: string;
  readonly title: string;
  /**
   * The date of the document's last material change. The server's copy is the
   * authority: a newer version fetched here makes every device agree again,
   * with no app release (`legal/agreement.ts`).
   */
  readonly version: string;
  readonly effective_date: string | null;
  readonly body_markdown: string;
  readonly body_html: string;
}

export function bundledDocument(policy: 'privacy' | 'terms'): LegalDocument {
  const slug = policy === 'privacy' ? 'privacy_policy' : 'terms_of_service';
  const entry = bundledDocuments.find((document) => document.slug === slug);
  if (entry === undefined) throw new Error(`Missing bundled policy: ${slug}`);
  return entry;
}

function cacheKey(slug: string): string {
  return `cribbage.legal.v1:${inferBaseUrl().replace(/\/$/, '')}:${slug}`;
}

/** Reject unexpected markup, including in local storage. Rails owns rendering. */
function parseDocument(value: unknown, slug: string): LegalDocument | null {
  if (typeof value !== 'object' || value === null) return null;
  const entry = value as Record<string, unknown>;
  if (
    entry.slug !== slug ||
    typeof entry.title !== 'string' ||
    !entry.title.trim() ||
    typeof entry.version !== 'string' ||
    !/^\d{4}-\d{2}-\d{2}$/.test(entry.version) ||
    typeof entry.body_markdown !== 'string' ||
    !entry.body_markdown.trim() ||
    typeof entry.body_html !== 'string' ||
    !entry.body_html.trim() ||
    !(
      entry.effective_date === null ||
      (typeof entry.effective_date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(entry.effective_date))
    )
  )
    return null;

  const html = new DOMParser().parseFromString(entry.body_html, 'text/html');
  const tags = new Set(
    'H1 H2 H3 H4 P BR BLOCKQUOTE UL OL LI A STRONG EM CODE TABLE THEAD TBODY TR TH TD'.split(' '),
  );
  if (!html.body.querySelector('h1') || html.head.children.length > 0) return null;
  for (const element of html.body.querySelectorAll('*')) {
    if (!tags.has(element.tagName)) return null;
    for (const attribute of element.attributes) {
      if (!['href', 'id', 'title'].includes(attribute.name)) return null;
      if (attribute.name === 'href') {
        // Validate relative links independently of Capacitor's custom origin.
        // This base is never requested or written into the document.
        const url = new URL(attribute.value, 'https://policy.invalid');
        if (!['http:', 'https:', 'mailto:'].includes(url.protocol)) return null;
      }
    }
  }
  return {
    slug,
    title: entry.title,
    version: entry.version,
    effective_date: entry.effective_date,
    body_markdown: entry.body_markdown,
    body_html: html.body.innerHTML,
  };
}

/**
 * Fetched copies a full or blocked store refused, kept for this visit. The
 * player reads what was fetched, and agrees to that same version, whether or
 * not it could be written down.
 */
const unsaved = new Map<string, LegalDocument>();

function storedDocument(bundle: LegalDocument): LegalDocument | null {
  try {
    const raw = localStorage.getItem(cacheKey(bundle.slug));
    if (!raw) return null;
    const saved = JSON.parse(raw) as { bundledMarkdown?: unknown; document?: unknown } | null;
    // A new bundled draft invalidates copies saved by older app releases.
    if (saved?.bundledMarkdown !== bundle.body_markdown) return null;
    return parseDocument(saved.document, bundle.slug);
  } catch {
    return null;
  }
}

/**
 * The copy of a document this device reads, and so the version a player
 * agrees to: the newest one fetched, with the bundle as the floor. A release
 * can ship a version before Rails serves it, and then the bundle is what the
 * player reads and agrees to. `YYYY-MM-DD` versions compare as text.
 */
export function savedDocument(bundle: LegalDocument): LegalDocument {
  const kept = unsaved.get(cacheKey(bundle.slug)) ?? storedDocument(bundle);
  if (kept === null || kept.version < bundle.version) return bundle;
  return kept;
}

export function saveDocument(bundle: LegalDocument, document: LegalDocument): void {
  // A late or stale reply never replaces a newer copy, or the bundle.
  if (document.version < savedDocument(bundle).version) return;
  const key = cacheKey(bundle.slug);
  try {
    localStorage.setItem(key, JSON.stringify({ bundledMarkdown: bundle.body_markdown, document }));
    unsaved.delete(key);
  } catch {
    // Private browsing or a full store must not prevent reading the live copy.
    unsaved.set(key, document);
  }
}

export async function fetchDocument(slug: string, signal: AbortSignal): Promise<LegalDocument> {
  const baseUrl = inferBaseUrl().replace(/\/$/, '');
  const response = await fetch(`${baseUrl}/api/v1/legal_documents/${slug}`, {
    headers: { accept: 'application/json' },
    credentials: 'omit',
    cache: 'no-cache',
    signal,
  });
  if (!response.ok) throw new Error('Policy request failed');
  const payload = (await response.json()) as { legal_document?: unknown } | null;
  const document = parseDocument(payload?.legal_document, slug);
  if (!document) throw new Error('Invalid policy document');
  return document;
}
